import { Check, ChevronDown, ChevronRight, ChevronUp, TriangleAlert, X } from "lucide-react";
import { useMemo } from "react";
import { Link, useSearchParams } from "react-router";

import { StatusPill, type StatusPillTone } from "nazare-ui/components";

import { MaximisedTablePage } from "../../../../maximised-table-page";
import {
  formatCostUsd,
  formatDurationMs,
  formatRelativeToNow,
  formatTokens,
  SessionExpiredError,
  useQaRunsInfiniteList,
  type RunLifecycleStatus,
  type RunSummary,
} from "../../../../qa-automation-api";
import { useDocumentTitle } from "../../../../use-document-title";
import { InfiniteScrollSentinel, SkeletonRow } from "../../../../skeleton-row";
import { useLoadMoreSentinel } from "../../../../use-load-more-sentinel";
import { useScrollEdges } from "../../../../use-scroll-edges";
import styles from "./recent-runs.module.css";

/**
 * Full-page `/qa-automation/recent-runs` route: the "maximised" table view
 * of the QA automation dashboard's `RecentRunsTable` card
 * (`../recent-runs.tsx`), opened via a grow transition from that card
 * (shared `viewTransitionName: "qa-card-recent-runs"`). Real BFF data via
 * `useQaRunsInfiniteList` (`../../../../qa-automation-api.ts`) — the BFF's
 * `/runs` route supports cursor pagination (`nextCursor`), so this page
 * paginates via `useInfiniteQuery` + `useLoadMoreSentinel`. The dashboard card
 * keeps using the simpler, unpaginated `useQaRunsList`.
 */

const STATUS_META: Record<RunLifecycleStatus, { label: string; tone: StatusPillTone }> = {
  queued: { label: "Queued", tone: "neutral" },
  running: { label: "Running", tone: "info" },
  passed: { label: "Passed", tone: "success" },
  failed: { label: "Failed", tone: "error" },
  abandoned: { label: "Abandoned", tone: "neutral" },
};

/** Only the "When" column is sortable today — `sort` is still
 * `"<column>:<direction>"` shaped (not just a bare direction), same
 * convention `../../notifications/index.tsx`'s own `sort` param uses, so
 * adding a second sortable column later doesn't need a different param,
 * just a different `<column>` value here. Defaults to `"desc"` (most
 * recent first — `useSortableRows`'s own previous `initialDirection`)
 * whenever the param is absent, unrecognised, or names a different column,
 * and a `desc` sort is never written to the URL at all (see `toggleSort`
 * below) — it's the default, so the URL for the common case stays bare. */
type SortDirection = "asc" | "desc";
const SORT_COLUMN_WHEN = "when";

function decodeSort(param: string | null): SortDirection {
  if (!param) return "desc";
  const [column, direction] = param.split(":");
  if (column !== SORT_COLUMN_WHEN) return "desc";
  return direction === "asc" ? "asc" : "desc";
}

function EnvPill({ env }: { env: string }) {
  return <span className={styles.envPill}>{env}</span>;
}

function CountsBreakdown({ counts }: { counts: RunSummary["counts"] }) {
  return (
    <span className={styles.counts}>
      <Check size={11} strokeWidth={2.5} aria-hidden />
      {counts.pass}
      <span aria-hidden>·</span>
      <TriangleAlert size={11} strokeWidth={2.5} aria-hidden />
      {counts.passWithSubFinding}
      <span aria-hidden>·</span>
      <X size={11} strokeWidth={2.5} aria-hidden />
      {counts.failRegression}
    </span>
  );
}

export function QaRecentRunsPage() {
  useDocumentTitle("Recent runs");

  const { ref: tableWrapRef, atTop, atBottom } = useScrollEdges<HTMLDivElement>();
  const {
    data,
    isLoading,
    isFetching,
    error,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useQaRunsInfiniteList();
  // Flattened across every page fetched so far — filtering/sorting below
  // applies to whatever's been loaded so far, same as any client-side
  // sort over an infinite-scrolled list (it doesn't fetch ahead).
  const runs = useMemo(() => data?.pages.flatMap((page) => page.runs) ?? [], [data]);
  // Arriving here via the dashboard card's "View all" shows that card's
  // rows INSTANTLY (`useQaRunsInfiniteList`'s `initialData` seed — see its
  // own doc comment in `qa-automation-api.ts`), so `isLoading` is already
  // `false` on that first render; this query still kicks off its own real
  // (larger, paginated) background fetch to replace the seeded snapshot,
  // and `isFetching` is exactly that fetch. `!isFetchingNextPage` excludes
  // "load more" (infinite-scroll) fetches, which already have their own
  // skeleton rows at the bottom of the table below. `!isLoading` excludes
  // the genuine cold-start case (nothing seeded, no dashboard visit this
  // session), which already gets the normal full-table skeleton instead.
  const showLoadingBar = isFetching && !isFetchingNextPage && !isLoading;

  const sentinelRef = useLoadMoreSentinel({
    hasMore: hasNextPage,
    isLoadingMore: isFetchingNextPage,
    onLoadMore: fetchNextPage,
    scrollRootRef: tableWrapRef,
  });

  // The "When" column's sort direction lives in the URL (`sort` param), not
  // `useSortableRows`'s own local `useState` — a sorted view of this page is
  // worth linking to/sharing/surviving a reload, same reasoning
  // `../../notifications/index.tsx`'s `sort` param follows (see that file's
  // own extended doc comment). This is purely a client-side re-sort of
  // whatever's already been fetched, same as `useSortableRows` did — the
  // BFF's `/runs` route has no server-side sort param, and `runs` (the
  // fetched pages) doesn't change when `sort` changes, so no new request
  // fires and `showLoadingBar` above is unaffected.
  const [searchParams, setSearchParams] = useSearchParams();
  const direction = useMemo(() => decodeSort(searchParams.get("sort")), [searchParams]);
  const sorted = useMemo(() => {
    const copy = [...runs];
    copy.sort((a, b) => {
      const av = new Date(a.startedAt).getTime();
      const bv = new Date(b.startedAt).getTime();
      if (av < bv) return direction === "asc" ? -1 : 1;
      if (av > bv) return direction === "asc" ? 1 : -1;
      return 0;
    });
    return copy;
  }, [runs, direction]);
  const toggle = () =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        const nextDirection: SortDirection = decodeSort(prev.get("sort")) === "desc" ? "asc" : "desc";
        if (nextDirection === "desc") next.delete("sort");
        else next.set("sort", `${SORT_COLUMN_WHEN}:${nextDirection}`);
        return next;
      },
      { replace: true },
    );

  return (
    <MaximisedTablePage
      title="Recent runs"
      count={hasNextPage ? `${sorted.length} runs loaded` : `${sorted.length} runs`}
      viewTransitionName="qa-card-recent-runs"
      minimiseTo="/qa-automation"
      tableWrapRef={tableWrapRef}
      atTop={atTop}
      atBottom={atBottom}
      showLoadingBar={showLoadingBar}
    >
      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.th} scope="col">
              <button
                type="button"
                className={styles.thSort}
                onClick={toggle}
                aria-label={`Sort by When, currently ${direction === "asc" ? "oldest first" : "newest first"}`}
              >
                When
                {direction === "asc" ? (
                  <ChevronUp size={12} aria-hidden />
                ) : (
                  <ChevronDown size={12} aria-hidden />
                )}
              </button>
            </th>
            <th className={styles.th} scope="col">
              Env
            </th>
            <th className={styles.th} scope="col">
              User
            </th>
            <th className={styles.th} scope="col">
              Project
            </th>
            <th className={styles.th} scope="col">
              Status
            </th>
            <th className={styles.th} scope="col">
              Duration
            </th>
            <th className={styles.th} scope="col">
              Est. cost
            </th>
            <th className={styles.th} scope="col" aria-hidden />
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            Array.from({ length: 8 }, (_, index) => <SkeletonRow key={`skeleton-${index}`} columns={8} />)
          ) : error ? (
            <tr>
              <td colSpan={8} className={styles.td}>
                {error instanceof SessionExpiredError
                  ? "Your session has expired — sign in to the main app in this browser, then reload."
                  : "Couldn't load recent runs."}
              </td>
            </tr>
          ) : sorted.length === 0 ? (
            <tr>
              <td colSpan={8} className={styles.td}>
                No runs in the selected window.
              </td>
            </tr>
          ) : (
            sorted.map((run) => (
              <tr key={run.runId} className={styles.row}>
                <td className={styles.td}>
                  {/* Stretched-link pattern: a `<tr>` can't be wrapped
                     in an `<a>` (invalid HTML), so this empty,
                     visually-hidden `<Link>` — rendered as the first
                     child of the row's first `<td>` — is absolutely
                     positioned to cover the entire row (`.rowLink` =
                     `position: absolute; inset: 0`, `.row` =
                     `position: relative`). A real `<a href>` for the
                     whole row, so cmd/ctrl/middle-click all work,
                     not just an onClick handler. */}
                  <Link
                    to={`/qa-automation/run/${run.runId}`}
                    viewTransition
                    className={styles.rowLink}
                    aria-label={`View run ${run.runId}`}
                  />
                  <span className={styles.whenLink}>{formatRelativeToNow(run.startedAt)}</span>
                </td>
                <td className={styles.td}>
                  <EnvPill env={run.environment.label} />
                </td>
                <td className={styles.td}>
                  <span className={styles.userCell}>{run.user.email ?? "(no auth)"}</span>
                </td>
                <td className={`${styles.td} ${styles.projectCell}`}>{run.project}</td>
                <td className={styles.td}>
                  <div className={styles.statusCell}>
                    <StatusPill tone={STATUS_META[run.status].tone}>
                      {STATUS_META[run.status].label}
                    </StatusPill>
                    <CountsBreakdown counts={run.counts} />
                  </div>
                </td>
                <td className={`${styles.td} ${styles.durationCell}`}>{formatDurationMs(run.durationMs)}</td>
                <td className={styles.td}>
                  <div className={styles.costCell}>{formatCostUsd(run.costUsd)}</div>
                  <div className={styles.tokensCell}>
                    {formatTokens(run.tokens?.input)} in · {formatTokens(run.tokens?.output)} out
                  </div>
                </td>
                <td className={`${styles.td} ${styles.actionCell}`}>
                  <span className={styles.viewLink}>
                    View
                    <ChevronRight size={13} aria-hidden />
                  </span>
                </td>
              </tr>
            ))
          )}
          {!isLoading && !error && (
            <>
              {hasNextPage && (
                <InfiniteScrollSentinel sentinelRef={sentinelRef} columns={8} />
              )}
              {isFetchingNextPage &&
                Array.from({ length: 3 }, (_, index) => (
                  <SkeletonRow key={`more-skeleton-${index}`} columns={8} />
                ))}
            </>
          )}
        </tbody>
      </table>
    </MaximisedTablePage>
  );
}
